import type { ConfigOutputDTO, ConfigurationFlowId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'
import { isOneItemPerVisual, requiresSourceFiles } from 'utils/purchaseFlowRevamp'

// The pure predicate map, never `modules/registry.ts`: that would pull React components into the store and cycle back here.
import { FLOW_IS_COMPLETE } from '../../modules/completeness'
import { floorPlanEditingScopeError, type FloorPlanConfigDraftDTO } from '../../modules/floorPlanConfig/floorPlanConfig.dto'
import { resolveTabsFromCartAndCatalogue } from '../../steps/configuration/resolveTabs'
import { shootOutputLines } from '../shootOutput'
import { makeConfigKey } from '../slices/productConfigSlice'
import type { PfStoreState } from '../store'
import { selectHasUnresolvedCartItems } from './cart'

/**
 * Flows whose configurator collects more than one flow's worth of data, and so
 * are only complete when every one of them is. The authorities document is the
 * case: one tab, one Save, two pages (power of attorney + property information),
 * each writing under its own key. Without this the Save button would light up
 * with the property-information page blank, and the order validator — which
 * rejects a document order carrying no land-registration sheets — would be the
 * one to say so, after payment details.
 */
const FLOW_ALSO_REQUIRES: Partial<Record<ConfigurationFlowId, ConfigurationFlowId[]>> = {
  power_of_attorney: ['property_info'],
}

/**
 * Flows whose predicate cannot tell whether the client has engaged with the page,
 * so opening it is what marks it done.
 *
 * `power_of_attorney` is the case: uploading a power of attorney is optional, and
 * the (legacy-faithful) predicate answers `true` for a line nobody has touched.
 * Left at that, a document tab would be half-confirmed before the client had seen
 * the upload at all.
 */
const FLOW_REQUIRES_VISIT: ReadonlySet<ConfigurationFlowId> = new Set<ConfigurationFlowId>([
  'power_of_attorney',
])

/**
 * Flows that configure one entry per photo the line claims, keyed by the photo's filename. A claimed photo
 * gets its entry only once its preview is ready, so until then the line is not complete although every
 * entry present is.
 */
const FLOW_COVERS_CLAIMED_PHOTOS: ReadonlySet<ConfigurationFlowId> = new Set<ConfigurationFlowId>([
  'staging',
])

const coversClaimedPhotos = (state: PfStoreState, cartItemId: string, dto: ConfigOutputDTO | undefined): boolean => {
  const entries = (dto as { rooms?: Record<string, unknown> } | undefined)?.rooms ?? {}
  return (state.attachments[cartItemId] ?? []).every((attachment) => !!entries[attachment.gcFilename])
}

/** Sky replacement switched on asks which sky; the line's AI settings live on the cart item, not in the flow's DTO. */
const aiSettingsComplete = (state: PfStoreState, cartItemId: string): boolean => {
  const aiEditing = state.items.find((item) => item.id === cartItemId)?.aiEditing
  return !aiEditing?.skyReplacement || !!aiEditing.cloudType
}

/** The completeness rules that depend on the cart line (its claimed photos, its AI settings) rather than the flow's DTO. */
const lineRulesHold = (state: PfStoreState, cartItemId: string, flowId: ConfigurationFlowId): boolean => {
  if (FLOW_COVERS_CLAIMED_PHOTOS.has(flowId) && !coversClaimedPhotos(state, cartItemId, state.productConfigs[makeConfigKey(cartItemId, flowId)])) return false
  if (flowId === 'editing_config' && !aiSettingsComplete(state, cartItemId)) return false
  return true
}

/** One flow's OWN data, ignoring anything its configurator also collects. What a
 *  page inside a multi-page configurator reports for its own badge. */
export const selectFlowSelfComplete =
  (cartItemId: string, flowId: ConfigurationFlowId) =>
  (state: PfStoreState): boolean => {
    const isComplete = FLOW_IS_COMPLETE[flowId]
    if (!isComplete) return false
    const dto = state.productConfigs[makeConfigKey(cartItemId, flowId)]
    if (!lineRulesHold(state, cartItemId, flowId)) return false
    return isComplete(dto)
  }

/**
 * Whether the client has seen a page that needs a visit. Visits are not saved with a draft, but a tab the client
 * confirmed was seen whole, so a resumed draft must not ask for the visit again.
 */
const wasVisited = (state: PfStoreState, cartItemId: string, tabFlowId: ConfigurationFlowId, pageFlowId: ConfigurationFlowId): boolean =>
  !!state.configPagesVisited[cartItemId]?.[pageFlowId] || state.cartItemUserConfirmed[cartItemId]?.[tabFlowId] === true

/** Whether a line's flow is complete: its own data, every flow its configurator also collects, and a visit where one is required. */
export const selectFlowComplete =
  (cartItemId: string, flowId: ConfigurationFlowId) =>
  (state: PfStoreState): boolean =>
    [flowId, ...(FLOW_ALSO_REQUIRES[flowId] ?? [])].every((id) => {
      if (FLOW_REQUIRES_VISIT.has(id) && !wasVisited(state, cartItemId, flowId, id)) return false
      return selectFlowSelfComplete(cartItemId, id)(state)
    })

type ConfigurationCatalogue = {
  shellProducts: Array<{
    id: string
    requiresConfiguration: boolean
    configurationFlows?: ConfigurationFlowId[]
    productType?: string
    kind?: string
    productGroupCode?: string
    variants?: Array<{ id: string; kind?: string; configurationFlows?: ConfigurationFlowId[]; inputImagePolicy?: string }>
  }>
}

/**
 * What a line still lacks of the source files its product takes, or null. A line made from the
 * order's shoot (`shootOutputLines`) takes none: its source is the photos the shoot delivers.
 */
const sourceFilesError = (
  catalogue: ConfigurationCatalogue,
  state: PfStoreState,
  item: PfStoreState['items'][number]
): 'images_missing' | 'too_many_images' | null => {
  if (shootOutputLines(state.items, catalogue.shellProducts).has(item.id)) return null
  const shell = catalogue.shellProducts.find((sp) => sp.id === item?.shellProductId)
  const variant = shell?.variants?.find((v) => v.id === item?.variantId)
  const policy = variant?.inputImagePolicy as InputImagePolicyV2 | undefined
  if (!requiresSourceFiles(policy)) return null
  const fileCount = state.attachments[item.id]?.length ?? 0
  // Placement sends none of a one-per-item line's files when the count disagrees with the quantity.
  if (isOneItemPerVisual(policy)) {
    if (fileCount < item.quantity) return 'images_missing'
    return fileCount > item.quantity ? 'too_many_images' : null
  }
  return fileCount > 0 ? null : 'images_missing'
}

/**
 * The Configuration step's gate: every `(cartItem, flow)` tab the page renders was saved by the client
 * (`cartItemUserConfirmed`, not merely present), and every line whose product takes source files has
 * them. Tabs are resolved by the same function the page uses, with the same `allowFallback`, so the
 * gate and the tab list cannot disagree.
 *
 * Source files are checked per line here rather than in a flow's predicate, because the requirement is
 * a catalogue fact (`inputImagePolicy`): one flow can need a file for one product and none for another.
 * A one-per-item product needs exactly one file per item, the same rule placement applies.
 */
export const selectConfigurationStepValid =
  (catalogue: ConfigurationCatalogue | undefined, allowFallback = false) =>
    (state: PfStoreState): boolean => {
      if (!catalogue) return false
      // A product the catalogue no longer offers cannot be ordered, whatever else is filled in.
      if (selectHasUnresolvedCartItems(catalogue)(state)) return false
      const tuples = resolveTabsFromCartAndCatalogue(state.items, catalogue.shellProducts, allowFallback)
      if (tuples.length === 0) return true
      // The rules that read the line rather than the tab's own data are checked here too: a tab is re-checked
      // only while its page is open, so a resumed draft confirmed before them would carry its flag past them.
      const confirmed = tuples.every(
        ({ cartItemId, flowId }) =>
          state.cartItemUserConfirmed[cartItemId]?.[flowId] === true && lineRulesHold(state, cartItemId, flowId)
      )
      if (!confirmed) return false

      // Per line, not per tab: an order-level flow contributes one tab for the whole cart.
      return state.items.every((item) => sourceFilesError(catalogue, state, item) === null)
    }

/**
 * The configuration tabs still to do, by the chip's own rule: not saved yet, or saved but no longer complete
 * (`selectFlowComplete` covers the line rules and the pages a flow must have been visited on), so the hint
 * names exactly the chips drawn as unfinished.
 * Joined to one `cartItemId|flowId,…` string so a subscriber re-renders only when the set changes.
 */
export const selectPendingConfigurationTabKeys =
  (catalogue: ConfigurationCatalogue | undefined, allowFallback = false) =>
    (state: PfStoreState): string => {
      if (!catalogue) return ''
      return resolveTabsFromCartAndCatalogue(state.items, catalogue.shellProducts, allowFallback)
        .filter(({ cartItemId, flowId }) =>
          !(state.cartItemUserConfirmed[cartItemId]?.[flowId] === true && selectFlowComplete(cartItemId, flowId)(state)))
        .map(({ cartItemId, flowId }) => `${cartItemId}|${flowId}`)
        .join(',')
    }

/**
 * Why the Configuration gate is closed, as `validation_errors` codes; empty when it is open. Mirrors
 * `selectConfigurationStepValid` rule for rule, so change the two together: a tab not saved yet is
 * `step_incomplete` (legacy's code for its configuration steps), unless a rule on the line explains it.
 */
export const selectConfigurationStepErrors =
  (catalogue: ConfigurationCatalogue | undefined, allowFallback = false) =>
    (state: PfStoreState): NextStepValidationError[] => {
      if (!catalogue) return ['step_incomplete']
      if (selectHasUnresolvedCartItems(catalogue)(state)) return ['product_unavailable']
      const tuples = resolveTabsFromCartAndCatalogue(state.items, catalogue.shellProducts, allowFallback)
      if (tuples.length === 0) return []

      const errors = new Set<NextStepValidationError>()
      for (const { cartItemId, flowId } of tuples) {
        // The floor plan editing question names what is missing, as legacy's floor_plan_unit_* codes did.
        const editingScopeError = flowId === 'floor_plan_config'
          ? floorPlanEditingScopeError(state.productConfigs[makeConfigKey(cartItemId, flowId)] as Partial<FloorPlanConfigDraftDTO> | undefined)
          : null
        if (FLOW_COVERS_CLAIMED_PHOTOS.has(flowId) && !coversClaimedPhotos(state, cartItemId, state.productConfigs[makeConfigKey(cartItemId, flowId)])) {
          errors.add('rooms_incomplete')
        } else if (flowId === 'editing_config' && !aiSettingsComplete(state, cartItemId)) {
          errors.add('sky_replacement_missing')
        } else if (editingScopeError) {
          errors.add(editingScopeError)
        } else if (state.cartItemUserConfirmed[cartItemId]?.[flowId] !== true) {
          errors.add('step_incomplete')
        }
      }
      if (errors.size > 0) return Array.from(errors)

      for (const item of state.items) {
        const error = sourceFilesError(catalogue, state, item)
        if (error) errors.add(error)
      }
      return Array.from(errors)
    }
